import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Calendar, Mail, Phone, User, Building, MessageSquare, ArrowRight, Tag, CheckCircle, Clock, X, AlertCircle, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { trackFormOpen, trackFormAbandon, trackFormSubmit, captureLeadInfo } from './OfferBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AppointmentSection = ({ isOpen, onClose, interestedPlan }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: '', message: '',
    preferredDate: '', preferredTime: '', discountCode: '',
    marketingConsent: false
  });
  const [discountApplied, setDiscountApplied] = useState(false);
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasTrackedOpen = useRef(false);
  const hasStartedFilling = useRef(false);

  // Generate next 7 working days (Mon-Sat)
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);
    currentDate.setDate(currentDate.getDate() + 1);
    
    while (dates.length < 7) {
      if (currentDate.getDay() !== 0) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }, []);

  // Fetch available slots when date changes
  const fetchSlots = useCallback(async (dateStr) => {
    if (!dateStr) return;
    setLoadingSlots(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/appointments/available-slots/${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        setSlotsData(data);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (formData.preferredDate) {
      fetchSlots(formData.preferredDate);
      setFormData(prev => ({ ...prev, preferredTime: '' }));
    }
  }, [formData.preferredDate, fetchSlots]);

  // Format time for display
  const formatTime = (time24) => {
    const [h, m] = time24.split(':').map(Number);
    const hour12 = h > 12 ? h - 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      trackFormOpen();
      hasTrackedOpen.current = true;
    }
    if (!isOpen) hasTrackedOpen.current = false;
  }, [isOpen]);

  const handleClose = () => {
    if (hasStartedFilling.current && (formData.name || formData.email || formData.phone)) {
      trackFormAbandon();
    }
    hasStartedFilling.current = false;
    if (onClose) onClose();
  };

  const handleFieldBlur = (fieldName) => {
    hasStartedFilling.current = true;
    if ((fieldName === 'email' && formData.email) || (fieldName === 'phone' && formData.phone) ||
        (fieldName === 'name' && formData.name && (formData.email || formData.phone))) {
      captureLeadInfo({ email: formData.email, name: formData.name, phone: formData.phone });
    }
  };

  const applyDiscountCode = () => {
    if (formData.discountCode.toUpperCase() === 'SUCCESS20') {
      setDiscountApplied(true);
      toast({ title: "Discount Applied!", description: "20% discount will be applied to your consultation package." });
    } else if (formData.discountCode) {
      toast({ title: "Invalid Code", description: "Please check your discount code and try again.", variant: "destructive" });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.preferredDate || !formData.preferredTime) return;
    setSubmitting(true);
    try {
      const combinedDateTime = formData.preferredDate && formData.preferredTime
        ? `${formData.preferredDate}T${formData.preferredTime}:00`
        : '';
      
      const appointmentData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        company: formData.company,
        message: `${interestedPlan ? `[Interested in: ${interestedPlan}] ` : ''}${formData.service ? `[Service: ${formData.service}] ` : ''}${formData.message}${discountApplied ? '\n\n[DISCOUNT CODE: SUCCESS20 - 20% OFF Applied]' : ''}`,
        preferredDate: combinedDateTime,
        interestedPlan: interestedPlan || ''
      };
      
      const response = await fetch(`${BACKEND_URL}/api/appointments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to book appointment');
      }

      toast({
        title: "Consultation Booked!",
        description: discountApplied
          ? "20% discount applied! We'll call you within 2 hours to confirm."
          : "We'll call you within 2 hours to confirm your 45-minute consultation slot."
      });

      trackFormSubmit();
      hasStartedFilling.current = false;
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '', preferredDate: '', preferredTime: '', discountCode: '', marketingConsent: false });
      setDiscountApplied(false);
      setSlotsData(null);
      if (onClose) onClose();
    } catch (error) {
      toast({ title: "Booking Failed", description: error.message || "An error occurred. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  const availableSlotCount = slotsData ? slotsData.available_count : 0;
  const bookedSlotCount = slotsData ? slotsData.booked_count : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={handleClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 sm:p-8 rounded-t-3xl text-white relative">
          <button onClick={handleClose} className="absolute top-4 right-4 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="appointment-title">Book Your Free Consultation</h2>
          <p className="text-emerald-50">45-minute personalized session with our CA experts</p>
          <div className="flex items-center gap-3 mt-4 text-sm">
            <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
              <Clock className="w-4 h-4" /> 45 min session
            </span>
            <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" /> 100% Free
            </span>
          </div>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="inline w-4 h-4 mr-1" /> Full Name *
              </label>
              <Input type="text" name="name" value={formData.name} onChange={handleChange}
                onBlur={() => handleFieldBlur('name')} placeholder="Enter your name" required
                data-testid="appointment-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Mail className="inline w-4 h-4 mr-1" /> Email *
              </label>
              <Input type="email" name="email" value={formData.email} onChange={handleChange}
                onBlur={() => handleFieldBlur('email')} placeholder="your@email.com" required
                data-testid="appointment-email" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="inline w-4 h-4 mr-1" /> Phone *
              </label>
              <Input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                onBlur={() => handleFieldBlur('phone')} placeholder="+91 XXXXX XXXXX" required
                data-testid="appointment-phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Building className="inline w-4 h-4 mr-1" /> Company Name
              </label>
              <Input type="text" name="company" value={formData.company} onChange={handleChange}
                placeholder="Your company name" data-testid="appointment-company" />
            </div>
          </div>

          {/* Service Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Briefcase className="inline w-4 h-4 mr-1" /> Service Interested In
            </label>
            <select
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="appointment-service"
            >
              <option value="">Select a service...</option>
              <option value="Virtual CFO Services">Virtual CFO Services</option>
              <option value="MIS Reporting & Dashboards">MIS Reporting & Dashboards</option>
              <option value="GST & Compliance">GST & Compliance</option>
              <option value="Fundraising & Investor Reporting">Fundraising & Investor Reporting</option>
              <option value="International Tax & Structuring">International Tax & Structuring</option>
              <option value="Bookkeeping & Accounting">Bookkeeping & Accounting</option>
              <option value="Audit & Assurance">Audit & Assurance</option>
              <option value="India Entry / FDI Advisory">India Entry / FDI Advisory</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-1" /> Select Date *
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {availableDates.map((date, index) => {
                const dateStr = date.toISOString().split('T')[0];
                const isSelected = formData.preferredDate === dateStr;
                return (
                  <button key={slot || `slot-${index}`} type="button"
                    data-testid={`date-slot-${index}`}
                    onClick={() => setFormData({ ...formData, preferredDate: dateStr })}
                    className={`p-2.5 rounded-xl text-center transition-all ${
                      isSelected
                        ? 'bg-emerald-600 text-white shadow-lg scale-105'
                        : 'bg-gray-100 hover:bg-emerald-50 text-gray-700 border border-transparent hover:border-emerald-300'
                    }`}
                  >
                    <div className="text-xs font-medium">{formatDate(date).split(',')[0]}</div>
                    <div className="text-lg font-bold">{date.getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Selection */}
          {formData.preferredDate && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Clock className="inline w-4 h-4 mr-1" /> Select Time * (IST)
                </label>
                {slotsData && (
                  <span className="text-xs text-orange-600 font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {availableSlotCount} of {slotsData.total_slots} slots available
                  </span>
                )}
              </div>

              {loadingSlots ? (
                <div className="flex items-center justify-center py-6 text-gray-400">
                  <Clock className="w-5 h-5 animate-spin mr-2" /> Loading slots...
                </div>
              ) : slotsData ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slotsData.slots.map((slot, index) => {
                    const isSelected = formData.preferredTime === slot.time;
                    const isBooked = slot.status === 'booked';
                    return (
                      <button key={slot || `slot-b-${index}`} type="button"
                        data-testid={`time-slot-${index}`}
                        disabled={isBooked}
                        onClick={() => !isBooked && setFormData({ ...formData, preferredTime: slot.time })}
                        className={`p-2.5 rounded-xl text-center transition-all ${
                          isBooked
                            ? 'bg-red-50 text-red-300 cursor-not-allowed border border-red-100 line-through'
                            : isSelected
                              ? 'bg-emerald-600 text-white shadow-lg scale-105'
                              : 'bg-gray-100 hover:bg-emerald-50 text-gray-700 border border-transparent hover:border-emerald-300'
                        }`}
                      >
                        <span className="text-sm font-medium">{formatTime(slot.time)}</span>
                        {isBooked && <div className="text-[10px] mt-0.5">Booked</div>}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MessageSquare className="inline w-4 h-4 mr-1" /> Tell us about your business
            </label>
            <Textarea name="message" value={formData.message} onChange={handleChange}
              placeholder="What challenges are you facing with your current finance operations?" rows={3}
              data-testid="appointment-message" />
          </div>

          {/* Discount Code */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="inline w-4 h-4 mr-1 text-orange-600" /> Have a Discount Code?
            </label>
            <div className="flex gap-2">
              <Input type="text" name="discountCode" value={formData.discountCode} onChange={handleChange}
                placeholder="Enter code (e.g., SUCCESS20)" className="flex-1 uppercase" disabled={discountApplied}
                data-testid="appointment-discount-code" />
              <Button type="button" onClick={applyDiscountCode} disabled={discountApplied}
                data-testid="apply-discount-btn"
                className={discountApplied ? "bg-green-600 text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}>
                {discountApplied ? <><CheckCircle className="w-4 h-4 mr-1" /> Applied!</> : "Apply"}
              </Button>
            </div>
            {discountApplied && (
              <p className="text-green-600 text-sm mt-2 font-medium">20% discount will be applied to your consultation package!</p>
            )}
          </div>

          {/* GDPR Consent */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="marketingConsent"
              checked={formData.marketingConsent}
              onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
              className="mt-1 w-4 h-4 accent-emerald-600 rounded"
              data-testid="marketing-consent-checkbox"
            />
            <label htmlFor="marketingConsent" className="text-xs text-gray-500 leading-relaxed">
              I agree to receive communications from Virtual CFO Consultancy about services, offers, and updates. 
              You can unsubscribe at any time. By submitting this form, you agree to our{' '}
              <a href="/privacy" className="text-emerald-600 underline">Privacy Policy</a> and{' '}
              <a href="/terms" className="text-emerald-600 underline">Terms of Service</a>.
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="button" data-testid="confirm-booking-btn"
              onClick={handleSubmit}
              disabled={!formData.name || !formData.email || !formData.phone || !formData.preferredDate || !formData.preferredTime || submitting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg rounded-full disabled:opacity-50">
              {submitting ? 'Booking...' : 'Confirm Booking'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button type="button" onClick={handleClose} variant="outline"
              className="sm:flex-none border-2 border-gray-300 text-gray-700 hover:bg-gray-50 py-6 px-8 rounded-full">
              Cancel
            </Button>
          </div>
          <p className="text-center text-xs text-gray-500">Your information is secure and will never be shared</p>
        </div>
      </div>
    </div>
  );
};
